import mixins from 'vue-typed-mixins';
import _ from 'lodash';

import { CourseProgress } from '@/openapi/user';

import BackButton from '@/user/components/header/backButton/BackButton.vue';
import ConfirmDialog from '@/user/components/dialog/confirm/ConfirmDialog.vue';
import Header from '@/user/components/header/Header.vue';
import RestErrorBar from '@/user/components/study/restErrorBar/RestErrorBar.vue';
import RestTimeBar from '@/user/components/study/restTimeBar/RestTimeBar.vue';
import StudyResultDialog from '@/user/components/study/dialog/result/StudyResultDialog.vue';

import CourseProgressMixin from '@/user/mixins/CourseProgressMixin';
import RoutesMixin from '@/user/mixins/RoutesMixin';
import StudyMixin from '@/user/mixins/StudyMixin';




/** お手つき回数 */
const INCORRECT_LIMIT = 2;

/** 画像ファイル拡張子 */
const IMAGE_FILE_EXT = 'png';

interface Choice {
  no: number;
  html: string;
}

enum QUESTION_INFO_KEY {
  FONT_SIZE,
  SENTENCE,
  WORD1,
  WORD2,
  WORD3
}

export default mixins(
  CourseProgressMixin,
  RoutesMixin,
  StudyMixin,
).extend({

  name: 'Word',

  components: {
    Header,
    BackButton,
    RestErrorBar,
    RestTimeBar,
    ConfirmDialog,
    StudyResultDialog,
  },

  data(): {
    visibleBackDialog: boolean;
    image: HTMLImageElement | null;
    choices: Choice[];
  } {
    return {
      visibleBackDialog: false,
      image: null,
      choices: [],
    };
  },

  computed: {
    questionInfos(): string[] {
      // 問題が取得できない場合は空文字の配列を返却（要素数をenumのkey数としたいが、Object.keys()でkeyとvalueを結合した配列が取れてしまうので/2している）
      if (this.question === null) return new Array(Object.keys(QUESTION_INFO_KEY).length / 2).fill('');

      return this.question.question.split(',');
    },
    sentence(): string {
      const sentence = this.questionInfos[QUESTION_INFO_KEY.SENTENCE];
      return sentence !== undefined ? this.$_createTextHtml(sentence) : '';
    },
    words(): string[] {
      return this.questionInfos.slice(QUESTION_INFO_KEY.WORD1);
    },
    wordCount(): number {
      return this.words[0].split(/　|\/n/g).length; // 全ての選択肢が同じ語数の想定
    },
    textSize(): string {
      return `textSize${this.questionInfos[QUESTION_INFO_KEY.FONT_SIZE]}`;
    },
    textPos(): string {
      return this.wordCount > 1 ? 'textPosLeft' : 'textPosCenter';
    }
  },

  async created(): Promise<void> {
    // font読み込み
    await this.loadFont();

    // 初期化
    this.$_initialize();
  },

  mounted(): void {
    this.image = this.$refs.image as HTMLImageElement;
  },

  destroyed(): void {
    this.clearRestTimeInterval();
    this.clearStudyVariable();
    this.clearStudyResult();
  },

  methods: {
    /**
     * 前の画面へ戻ります。
     */
    back(): void {
      this.visibleBackDialog = false;
      this.$router.push(this.routes.UnitSelect);
    },

    /**
     * 回答選択の処理をします。
     *
     * @param no 選択肢No
     */
    choose(no: number): void {
      // 答え合わせ
      const isCorrect = this.question!.right === String(no);

      // 誤答の学習メモを保存、不正解回数の更新
      if (!isCorrect) {
        this.incrementIncorretCount();
        this.addStudyMemo(String(this.question!.questionNo)); // TODO:何を格納するか確認
      }

      this.judgeScoreResult(false, isCorrect, INCORRECT_LIMIT);
    },

    // StudyMixinを上書き
    nextQuestion(): void {
      // 画像要素が取れていない場合はここで取る
      if (this.image === null) this.image = this.$refs.image as HTMLImageElement;

      // 表示初期化
      this.choices    = [];
      this.image!.src = '';

      // 問題設定
      this.forwardQuestionIndex();
      const tmpChoices: Choice[] = [];
      this.words.forEach((w, i) => tmpChoices.push({no: i, html: this.$_createTextHtml(w)}));
      this.choices = _.shuffle(tmpChoices);

      // 画像読み込み
      const q = this.question!.q;
      this.incrementMediaCount();
      this.image!.onload = () => this.loadMedia(); // 画像読み込み完了処理（タイマースタートもここ）
      this.image!.src    = q.substring(1, q.length - 1);
    },

    // StudyMixinを上書き、学習結果保存処理用
    isCourseComplete(isCorrect: boolean): boolean {
      // 不正解の場合はfalse
      if (!isCorrect) return false;

      // 回答中のユニット以外の全単元、ユニットが終了しているか
      for (const section of this.sections) {
        const progress  = this.progresses.find(s => s.sectionNo === section.sectionNo);
        // 単元内のユニット数（回答中の単元の場合はユニット数を減らしておく）
        const unitCount = section.sectionNo === this.studySectionNo ? section.unitCount - 1 : section.unitCount;
        // 単元の進捗が存在しない、もしくは単元内のユニット数より少ない場合はfalse
        if (progress === undefined || progress.unitNos.length < unitCount) return false;
      }

      return true;
    },

    // StudyMixinを上書き、学習結果保存処理用
    updateProgress(isCorrect: boolean): void {
      // 不正解の場合はreturn
      if (!isCorrect) return;

      // 進捗に学習中のユニットがない場合は追加
      const progress = this.getProgress();
      let   unitNos  = progress.unitNos;
      if (!unitNos.includes(this.studyUnitNo)) {
        unitNos.push(this.studyUnitNo);
        unitNos = _.sortBy(progress.unitNos);
      }

      // 進捗に学習中の単元がない場合は追加
      let progresses = this.progresses;
      if (!progresses.some(p => p.sectionNo === this.studySectionNo)) {
        progresses.push(progress);
        progresses = _.sortBy(progresses, p => p.sectionNo);
        this.setProgresses(progresses);
      }
    },

    // StudyMixinを上書き、学習結果保存処理用
    getProgress(): CourseProgress {
      const progress = this.progresses.find(p => p.sectionNo === this.studySectionNo);
      return progress ?? { sectionNo: this.studySectionNo, unitNos: [] };
    },

    // StudyMixinを上書き、学習終了後に学習続行用
    initializeCourseComponent(): void {
      this.initializeStudyVariable();
      this.$_initialize();
    },

    /**
     * 初期化処理をします。
     */
    async $_initialize(): Promise<void> {
      // 進捗、セクション読込
      await this.loadProgressesState(this.studyCourseId);

      // 初期化
      this.initializeStudyVariable();
      this.initializeStudyRequest();
      this.visibleBackDialog = false;
      this.image             = null;
      this.choices           = [];

      // 回答可能回数
      this.setIncorretLimit(INCORRECT_LIMIT);

      // ステージ名設定
      const stageName = this.$_createStageName();
      this.setStageName(stageName);

      // 問題取得
      await this.loadQuestions();
      this.$_addQuestionImages();

      // 問題開始
      this.initializeQuestionIndex();
      this.nextQuestion();
    },

    /**
     * 文章描画HTMLを生成します。
     *
     * @param word
     * @return 文章描画HTML
     */
    $_createTextHtml(word: string): string {
      let ret     = '';
      let tmpHtml = word.replace(/\/n/g, '<br />');

      for (let i = 0; i < tmpHtml.length; i++) {
        const char = tmpHtml.charAt(i);
        switch(char) {
          // 複数文字にまたがるルビ
          case '/':
            const multiRubyText = tmpHtml.slice(i + 1, tmpHtml.indexOf(')', i) + 1); // 先頭の/を除いて取得
            ret += this.$_createRubyTag(multiRubyText);
            i   += multiRubyText.length;
            break;

          // 1文字のルビ
          case '(':
            ret = ret.slice(0, -1); // 格納済みのルビ対象文字を削除
            const singleRubyText = tmpHtml.slice(i - 1, tmpHtml.indexOf(')', i) + 1); // 1文字前も含めて取得
            ret += this.$_createRubyTag(singleRubyText);
            i   += singleRubyText.length - 2;
            break;

          // タグ
          case '<':
            const tag = tmpHtml.slice(i, tmpHtml.indexOf('>', i) + 1);
            ret += tag;
            i   += tag.length - 1;
            break;

          default:
            ret += char;
            break;
        }
      }

      return ret;
    },

    /**
     * ルビ表示用のHTMLタグを生成します。
     *
     * @param text
     * @return ルビ表示用のHTMLタグ
     */
    $_createRubyTag(text: string): string {
      const rtStartIdx = text.indexOf('(');
      const ruby       = text.slice(0,              rtStartIdx);
      const rt         = text.slice(rtStartIdx + 1, text.length - 1);
      return `<ruby>${ruby}<rp>(</rp><rt>${rt}</rt><rp>)</rp></ruby>`;
    },

    /**
     * 問題画像パスを追加します。
     */
    $_addQuestionImages(): void {
      const sectionNoStr = String(this.studySectionNo).padStart(2, '0');
      this.questions.forEach(q => {
        const questionNoStr = String(q.questionNo).padStart(3, '0');
        q.q = this.$_getQuestionImageUrl(`section${sectionNoStr}`, `${sectionNoStr}-${questionNoStr}.${IMAGE_FILE_EXT}`);
      });
    },

    /**
     * ステージ名を生成します。
     *
     * @return ステージ名
     */
    $_createStageName(): string {
      return `${this.getSection(this.studySectionNo).sectionName} (${this.studyUnitNo})`;
    }
  }

});
